import React, { useState } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { ThumbUp, ThumbDown } from '@mui/icons-material';
import { sendBlockFeedback } from 'services/feedbackService';
import { openSnackbar } from 'store/reducers/snackbar';
import { useDispatch, useSelector } from 'react-redux';

const FeedbackButtons = ({
  blockId,
  blockType,
  blockTitle,
  blockContent,
  taskId,
  serviceName,
  showThankYouMessage = true,
  isVisible = false
}) => {
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [isHelpful, setIsHelpful] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useDispatch();
  const userPlan = useSelector((state) => state.proAccountReducer.plan);

  const handleFeedback = async (helpful) => {
    if (feedbackSubmitted || isSubmitting) return;

    setIsSubmitting(true);
    setIsHelpful(helpful);

    try {
      const feedbackData = {
        taskId,
        blockId,
        blockType,
        blockTitle,
        blockContent,
        isHelpful: helpful,
        serviceName,
        userPlan
      };

      await sendBlockFeedback(feedbackData);

      setFeedbackSubmitted(true);

      if (showThankYouMessage) {
        dispatch(
          openSnackbar({
            open: true,
            message: 'Thanks for your feedback!',
            variant: 'alert',
            alert: { color: 'success' },
            autoHideDuration: 2000
          })
        );
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      dispatch(
        openSnackbar({
          open: true,
          message: 'Failed to submit feedback. Please try again.',
          variant: 'alert',
          alert: { color: 'error' },
          autoHideDuration: 3000
        })
      );
      // Reset state on error
      setIsHelpful(null);
      setFeedbackSubmitted(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (feedbackSubmitted) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
        <Typography
          variant="body2"
          color={isHelpful ? 'success.main' : 'text.secondary'}
          sx={{
            fontSize: '0.75rem',
            fontWeight: 500,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5
          }}
        >
          {isHelpful ? (
            <>
              <ThumbUp sx={{ fontSize: 16 }} />
              Helpful
            </>
          ) : (
            <>
              <ThumbDown sx={{ fontSize: 16 }} />
              Not helpful
            </>
          )}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        opacity: isVisible ? 1 : 0,
        transition: 'opacity 0.2s ease'
      }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
        Was this helpful?
      </Typography>

      <Tooltip title="Yes, helpful">
        <IconButton
          size="small"
          onClick={() => handleFeedback(true)}
          disabled={isSubmitting}
          sx={{
            color: isHelpful === true ? 'success.main' : 'text.secondary',
            '&:hover': {
              color: 'text.primary',
              backgroundColor: 'action.hover'
            },
            transition: 'all 0.2s ease'
          }}
        >
          <ThumbUp sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>

      <Tooltip title="No, not helpful">
        <IconButton
          size="small"
          onClick={() => handleFeedback(false)}
          disabled={isSubmitting}
          sx={{
            color: isHelpful === false ? 'error.main' : 'text.secondary',
            '&:hover': {
              color: 'text.primary',
              backgroundColor: 'action.hover'
            },
            transition: 'all 0.2s ease'
          }}
        >
          <ThumbDown sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default FeedbackButtons;
